import { Fragment } from 'react'
import PropTypes from 'prop-types'
import { Box, Checkbox, Divider, FormControlLabel, Radio, RadioGroup, Stack, Typography } from '@mui/material'
import { PLANNED_JOB_LIST_CONFIGURABLE_COLUMNS } from './hooks/useJobColumns'
import {
  TAB_DEFINITIONS, TAB_KEYS, PRIMARY_LIST_KEYS, DEFAULT_VISIBLE_TABS, DEFAULT_PRIMARY_LIST,
  getEffectivePlannedByTechnologyIds
} from './utils/plannedJobTabs'
import useMosInfo from '../../common/provider/useMosInfo'
import useMosTechnologies from './hooks/useMosTechnologies'
import { SmartSelect } from '../../../../generic/smartSelect'

const CONFIGURABLE_COLUMN_FIELDS = new Set(PLANNED_JOB_LIST_CONFIGURABLE_COLUMNS.map((column) => column.field))

const PlannedJobListWidgetConfiguration = ({ data, setData, t_, showTitle = true }) => {
  const { globalData, setGlobalData } = useMosInfo()

  const hiddenColumns = Array.isArray(globalData?.plannedJobListHiddenColumns)
    ? globalData.plannedJobListHiddenColumns.filter((field) => CONFIGURABLE_COLUMN_FIELDS.has(field))
    : Array.isArray(data?.hiddenColumns)
      ? data.hiddenColumns.filter((field) => CONFIGURABLE_COLUMN_FIELDS.has(field))
      : []

  const handleVisibilityChange = (field, checked) => {
    const nextHiddenColumns = checked
      ? hiddenColumns.filter((hiddenField) => hiddenField !== field)
      : [...new Set([...hiddenColumns, field])]

    setGlobalData((prev) => ({ ...prev, plannedJobListHiddenColumns: nextHiddenColumns }))
    setData({
      ...(data || {}),
      hiddenColumns: nextHiddenColumns
    })
  }

  // HU-04 (S16): which of the modal tabs are visible, and which list is the widget's main view.
  // This panel is mounted by BOTH the Planned Jobs widget and the Operation widget, which open the
  // same modal, so the tab decision is shared through the MOS context exactly like the columns
  // above -- writing it only into `data` left each widget with its own private list.
  const visibleTabs = Array.isArray(globalData?.plannedJobListVisibleTabs)
    ? globalData.plannedJobListVisibleTabs
    : Array.isArray(data?.visibleTabs)
      ? data.visibleTabs
      : DEFAULT_VISIBLE_TABS
  // The main list stays per widget: only the Planned Jobs widget has one.
  const primaryList = typeof data?.primaryList === 'string' ? data.primaryList : DEFAULT_PRIMARY_LIST

  const handleTabToggle = (key, checked) => {
    const nextVisibleTabs = checked
      ? [...new Set([...visibleTabs, key])]
      : visibleTabs.filter((tabKey) => tabKey !== key)
    setGlobalData((prev) => ({ ...prev, plannedJobListVisibleTabs: nextVisibleTabs }))
    setData({ ...(data || {}), visibleTabs: nextVisibleTabs })
  }

  // Story 193 (S24-S28): the technologies of the "All Jobs" tab. Shown -- and the catalog requested
  // -- only while the tab is checked. Shared through the MOS context like the tabs, so the choice
  // holds for both widgets that mount this panel (RN6).
  const plannedByTechnologyEnabled = visibleTabs.includes(TAB_KEYS.PLANNED_BY_TECHNOLOGY)
  const { technologies } = useMosTechnologies({ enabled: plannedByTechnologyEnabled })
  const plannedByTechnologyIds = getEffectivePlannedByTechnologyIds(
    Array.isArray(globalData?.plannedJobListPlannedByTechnologyIds)
      ? globalData.plannedJobListPlannedByTechnologyIds
      : data?.plannedByTechnologyIds
  )
  // Only the active technologies of the selection are shown (S27). A deactivated one stays saved
  // until the selector changes; then what the selector shows is what gets saved (RN6.4).
  const selectedTechnologies = technologies.filter((technology) => plannedByTechnologyIds.includes(technology.id))

  const handleTechnologiesChange = (selected) => {
    const nextIds = getEffectivePlannedByTechnologyIds((Array.isArray(selected) ? selected : []).map((technology) => technology.id))
    setGlobalData((prev) => ({ ...prev, plannedJobListPlannedByTechnologyIds: nextIds }))
    setData({ ...(data || {}), plannedByTechnologyIds: nextIds })
  }

  const handlePrimaryListChange = (value) => {
    setData({ ...(data || {}), primaryList: value })
  }

  return (
    <Box sx={{ p: 2, minWidth: 260 }}>
      {showTitle && (
        <Typography variant='h6' sx={{ mb: 1 }}>
          {t_('Configuration')}
        </Typography>
      )}
      <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
        {t_('Job list columns visibility')}
      </Typography>
      <Stack>
        {PLANNED_JOB_LIST_CONFIGURABLE_COLUMNS.map((column) => (
          <FormControlLabel
            key={column.field}
            control={
              <Checkbox
                checked={!hiddenColumns.includes(column.field)}
                onChange={(event) => handleVisibilityChange(column.field, event.target.checked)}
                size='small'
              />
            }
            label={t_(column.label)}
          />
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
        {t_('Modal tabs')}
      </Typography>
      <Stack>
        {TAB_DEFINITIONS.map((tabDef) => (
          <Fragment key={tabDef.key}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={visibleTabs.includes(tabDef.key)}
                  onChange={(event) => handleTabToggle(tabDef.key, event.target.checked)}
                  size='small'
                />
              }
              label={t_(tabDef.label)}
            />
            {tabDef.key === TAB_KEYS.PLANNED_BY_TECHNOLOGY && plannedByTechnologyEnabled && (
              <Box sx={{ my: 1 }}>
                <SmartSelect
                  multiple
                  size='small'
                  label={t_('Technologies')}
                  selectableOptions={technologies}
                  value={selectedTechnologies}
                  onChange={handleTechnologiesChange}
                  autoSelectFirst={false}
                  disableClearable={false}
                />
              </Box>
            )}
          </Fragment>
        ))}
      </Stack>

      <Divider sx={{ my: 2 }} />

      <Typography variant='subtitle2' color='text.secondary' sx={{ mb: 1 }}>
        {t_('Main list')}
      </Typography>
      <RadioGroup
        value={primaryList}
        onChange={(event) => handlePrimaryListChange(event.target.value)}
      >
        <FormControlLabel value={PRIMARY_LIST_KEYS.DEFAULT} control={<Radio size='small' />} label={t_('Current queue')} />
        {TAB_DEFINITIONS.filter((tabDef) => visibleTabs.includes(tabDef.key)).map((tabDef) => (
          <FormControlLabel key={tabDef.key} value={tabDef.key} control={<Radio size='small' />} label={t_(tabDef.label)} />
        ))}
      </RadioGroup>
    </Box>
  )
}

PlannedJobListWidgetConfiguration.propTypes = {
  data: PropTypes.object,
  setData: PropTypes.func.isRequired,
  t_: PropTypes.func.isRequired,
  showTitle: PropTypes.bool
}

export default PlannedJobListWidgetConfiguration
